import { 
  Rect, Text, Image as KonvaImage, Group, Line, Circle as KonvaCircle, Ellipse 
} from 'react-konva';
import { GRID_SIZE } from './constants';

// Render grid overlay
export function renderGrid(moodBoard, showGrid) {
  if (!showGrid) return null;
  
  const lines = [];
  const width = moodBoard?.canvas_width || 1650;
  const height = moodBoard?.canvas_height || 1275;
  
  // Vertical lines
  for (let x = 0; x <= width; x += GRID_SIZE) {
    lines.push(
      <Line
        key={`v-${x}`}
        points={[x, 0, x, height]}
        stroke="#E5E2DD"
        strokeWidth={x % (GRID_SIZE * 5) === 0 ? 0.5 : 0.25}
        listening={false}
      />
    );
  }
  
  // Horizontal lines
  for (let y = 0; y <= height; y += GRID_SIZE) {
    lines.push(
      <Line
        key={`h-${y}`}
        points={[0, y, width, y]}
        stroke="#E5E2DD"
        strokeWidth={y % (GRID_SIZE * 5) === 0 ? 0.5 : 0.25}
        listening={false}
      />
    );
  }
  
  return lines;
}

// Render SpecNum tags for images
export function renderSpecTags({
  moodBoard,
  showSpecTags,
  specTagOpacity,
  specTagFontColor,
  specTagBgColor,
  specTagBorderColor,
  specTagShowBox,
}) {
  if (!showSpecTags) return null;
  
  return moodBoard?.elements
    ?.filter(el => el.element_type === 'image' && el.spec_num)
    .map(el => {
      const tagText = el.spec_num;
      const tagWidth = tagText.length * 8 + 12;
      const tagHeight = 20;
      
      return (
        <Group
          key={`tag-${el.id}`}
          x={el.x + 5}
          y={el.y + 5}
          opacity={specTagOpacity}
          listening={false}
        >
          {specTagShowBox && (
            <Rect
              width={tagWidth}
              height={tagHeight}
              fill={specTagBgColor}
              stroke={specTagBorderColor}
              strokeWidth={1}
              cornerRadius={3}
            />
          )}
          <Text
            x={specTagShowBox ? 6 : 0}
            y={specTagShowBox ? 4 : 0}
            text={tagText}
            fontSize={11}
            fontFamily="Manrope"
            fontStyle="bold"
            fill={specTagFontColor}
          />
        </Group>
      );
    });
}

// Render Legend box
export function renderLegend({
  moodBoard,
  showLegend,
  legendPosition,
  setLegendPosition,
}) {
  if (!showLegend) return null;
  
  const itemsWithSpecs = moodBoard?.elements
    ?.filter(el => el.element_type === 'image' && el.spec_num)
    .map(el => ({ spec_num: el.spec_num, title: el.clip_title || 'Untitled' }))
    .sort((a, b) => a.spec_num.localeCompare(b.spec_num)) || [];
  
  if (itemsWithSpecs.length === 0) return null;
  
  const lineHeight = 28;
  const padding = 16;
  const legendWidth = 320;
  const headerHeight = 36;
  const itemsHeight = itemsWithSpecs.length * lineHeight;
  const legendHeight = headerHeight + itemsHeight + padding * 2;
  
  return (
    <Group
      x={legendPosition.x}
      y={legendPosition.y}
      draggable={true}
      onDragEnd={(e) => {
        setLegendPosition({ x: e.target.x(), y: e.target.y() });
      }}
    >
      {/* Legend background */}
      <Rect
        width={legendWidth}
        height={legendHeight}
        fill="#FFFFFF"
        stroke="#D4D0C8"
        strokeWidth={1}
        shadowColor="rgba(0,0,0,0.12)"
        shadowBlur={12}
        shadowOffsetY={4}
        cornerRadius={8}
      />
      {/* Header */}
      <Rect
        width={legendWidth}
        height={headerHeight}
        fill="#F8F7F5"
        cornerRadius={[8, 8, 0, 0]}
      />
      <Text
        x={padding}
        y={12}
        text="SPECIFICATIONS"
        fontSize={12}
        fontFamily="Manrope"
        fontStyle="bold"
        fill="#78716C"
        letterSpacing={1.5}
      />
      {/* Legend items */}
      {itemsWithSpecs.map((item, i) => (
        <Group key={i} y={headerHeight + padding + (i * lineHeight)}>
          <Text
            x={padding}
            text={item.spec_num}
            fontSize={13}
            fontFamily="Manrope"
            fontStyle="bold"
            fill="#BC5D41"
            width={75}
          />
          <Text
            x={padding + 75}
            text={item.title.length > 35 ? item.title.substring(0, 35) + '...' : item.title}
            fontSize={12}
            fontFamily="Manrope"
            fill="#1A1918"
            width={legendWidth - padding * 2 - 75}
          />
        </Group>
      ))}
    </Group>
  );
}

// Render watermark
export function renderWatermark({
  moodBoard,
  showWatermark,
  watermarkOpacity,
  logoImage,
}) {
  if (!showWatermark || !logoImage) return null;
  
  const canvasW = moodBoard?.canvas_width || 1650;
  const canvasH = moodBoard?.canvas_height || 1275;
  const watermarkSize = Math.min(canvasW, canvasH) * 0.5;
  
  return (
    <KonvaImage
      image={logoImage}
      x={(canvasW - watermarkSize) / 2}
      y={(canvasH - watermarkSize) / 2}
      width={watermarkSize}
      height={watermarkSize}
      opacity={watermarkOpacity}
      listening={false}
    />
  );
}

// Helper function to get shadow props
function getShadowProps(el) {
  if (!el.shadow_enabled) return {};
  return {
    shadowColor: `rgba(0,0,0,${el.shadow_opacity || 0.3})`,
    shadowBlur: el.shadow_blur || 10,
    shadowOffsetX: el.shadow_offset_x || 3,
    shadowOffsetY: el.shadow_offset_y || 3,
    shadowEnabled: true,
  };
}

// Render a single element
export function renderElement({
  el,
  loadedImages,
  selectedId,
  activeTool,
  setSelectedId,
  updateElement,
  handleDragEnd,
  handleTransformEnd,
}) {
  const isSelected = selectedId === el.id;
  const elementOpacity = el.opacity !== undefined ? el.opacity : 1;
  const shadowProps = getShadowProps(el);
  
  // Render Image element
  if (el.element_type === 'image') {
    const img = loadedImages[el.image_url];
    if (!img) return null;
    
    // If image has crop_points, apply clipping
    if (el.crop_points && el.crop_points.length >= 3) {
      return (
        <Group
          key={el.id}
          id={el.id}
          x={el.x}
          y={el.y}
          width={el.width}
          height={el.height}
          rotation={el.rotation || 0}
          opacity={elementOpacity}
          draggable={activeTool === 'select'}
          onClick={() => setSelectedId(el.id)}
          onTap={() => setSelectedId(el.id)}
          onDragEnd={(e) => handleDragEnd(e, el.id)}
          onTransformEnd={(e) => handleTransformEnd(e, el.id)}
          clipFunc={(ctx) => {
            ctx.beginPath();
            el.crop_points.forEach((p, i) => {
              const x = p.x * el.width;
              const y = p.y * el.height;
              if (i === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            });
            ctx.closePath();
          }}
        >
          <KonvaImage
            image={img}
            width={el.width}
            height={el.height}
            {...shadowProps}
          />
        </Group>
      );
    }
    
    // Regular image (no crop)
    return (
      <KonvaImage
        key={el.id}
        id={el.id}
        image={img}
        x={el.x}
        y={el.y}
        width={el.width}
        height={el.height}
        rotation={el.rotation || 0}
        opacity={elementOpacity}
        draggable={activeTool === 'select'}
        onClick={() => setSelectedId(el.id)}
        onTap={() => setSelectedId(el.id)}
        onDragEnd={(e) => handleDragEnd(e, el.id)}
        onTransformEnd={(e) => handleTransformEnd(e, el.id)}
        {...shadowProps}
      />
    );
  }
  
  // Render Text element
  if (el.element_type === 'text') {
    const textHeight = el.height || 30;
    
    // Text without leader line
    if (!el.show_leader) {
      return (
        <Text
          key={el.id}
          id={el.id}
          x={el.x}
          y={el.y}
          width={el.width}
          text={el.text}
          fontSize={el.font_size}
          fontFamily={el.font_family}
          fontStyle={el.font_style === 'italic' ? 'italic' : 'normal'}
          fontVariant={el.font_weight === 'bold' ? 'bold' : 'normal'}
          fill={el.text_color}
          align={el.text_align || 'left'}
          rotation={el.rotation || 0}
          opacity={elementOpacity}
          draggable={activeTool === 'select'}
          onClick={() => setSelectedId(el.id)}
          onTap={() => setSelectedId(el.id)}
          onDragEnd={(e) => handleDragEnd(e, el.id)}
          onTransformEnd={(e) => handleTransformEnd(e, el.id)}
          onDblClick={() => {
            const newText = prompt('Edit text:', el.text);
            if (newText !== null) {
              updateElement(el.id, { text: newText });
            }
          }}
          {...shadowProps}
        />
      );
    }
    
    // Render text with leader line - Cubic bezier for S-curves
    const startX = el.leader_start_x ?? (el.width / 2);
    const startY = el.leader_start_y ?? (textHeight + 8);
    const curve1X = el.leader_curve1_x ?? (startX + 20);
    const curve1Y = el.leader_curve1_y ?? (startY + 30);
    const curve2X = el.leader_curve2_x ?? (startX - 20);
    const curve2Y = el.leader_curve2_y ?? (startY + 60);
    const endX = el.leader_end_x ?? (startX + 20);
    const endY = el.leader_end_y ?? (startY + 90);
    
    // Calculate arrow head direction (from second control point to end point)
    const arrowSize = 10;
    const angle = Math.atan2(endY - curve2Y, endX - curve2X);
    
    return (
      <Group
        key={el.id}
        id={el.id}
        x={el.x}
        y={el.y}
        rotation={el.rotation || 0}
        opacity={elementOpacity}
        draggable={activeTool === 'select'}
        onClick={() => setSelectedId(el.id)}
        onTap={() => setSelectedId(el.id)}
        onDragEnd={(e) => handleDragEnd(e, el.id)}
        onTransformEnd={(e) => {
          const node = e.target;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          node.scaleX(1);
          node.scaleY(1);
          updateElement(el.id, {
            x: node.x(),
            y: node.y(),
            width: Math.max(50, el.width * scaleX),
            rotation: node.rotation()
          });
        }}
      >
        {/* Text */}
        <Text
          width={el.width}
          text={el.text}
          fontSize={el.font_size}
          fontFamily={el.font_family}
          fontStyle={el.font_style === 'italic' ? 'italic' : 'normal'}
          fontVariant={el.font_weight === 'bold' ? 'bold' : 'normal'}
          fill={el.text_color}
          align={el.text_align || 'left'}
          onDblClick={() => {
            const newText = prompt('Edit text:', el.text);
            if (newText !== null) {
              updateElement(el.id, { text: newText });
            }
          }}
          {...shadowProps}
        />
        
        {/* Leader line - Cubic bezier curve for S-curves */}
        <Line
          points={[startX, startY]}
          stroke={el.leader_color || '#1A1918'}
          strokeWidth={el.leader_width || 2}
          lineCap="round"
          sceneFunc={(context, shape) => {
            context.beginPath();
            context.moveTo(startX, startY);
            context.bezierCurveTo(curve1X, curve1Y, curve2X, curve2Y, endX, endY);
            context.strokeShape(shape);
          }}
        />
        
        {/* Arrow head */}
        {el.show_arrow !== false && (
          <Line
            points={[
              endX - arrowSize * Math.cos(angle - Math.PI / 6),
              endY - arrowSize * Math.sin(angle - Math.PI / 6),
              endX,
              endY,
              endX - arrowSize * Math.cos(angle + Math.PI / 6),
              endY - arrowSize * Math.sin(angle + Math.PI / 6)
            ]}
            stroke={el.leader_color || '#1A1918'}
            strokeWidth={el.leader_width || 2}
            lineCap="round"
            lineJoin="round"
          />
        )}
        
        {/* Draggable control points (only when selected/editing) */}
        {isSelected && (
          <>
            {/* Start point (green) - movable origin */}
            <KonvaCircle
              x={startX}
              y={startY}
              radius={6}
              fill="#22C55E"
              stroke="#FFFFFF"
              strokeWidth={2}
              draggable={true}
              onDragMove={(e) => {
                updateElement(el.id, { 
                  leader_start_x: e.target.x(),
                  leader_start_y: e.target.y()
                });
              }}
            />
            
            {/* First curve control point (blue) */}
            <KonvaCircle
              x={curve1X}
              y={curve1Y}
              radius={6}
              fill="#3B82F6"
              stroke="#FFFFFF"
              strokeWidth={2}
              draggable={true}
              onDragMove={(e) => {
                updateElement(el.id, { 
                  leader_curve1_x: e.target.x(),
                  leader_curve1_y: e.target.y()
                });
              }}
            />
            
            {/* Second curve control point (purple) */}
            <KonvaCircle
              x={curve2X}
              y={curve2Y}
              radius={6}
              fill="#A855F7"
              stroke="#FFFFFF"
              strokeWidth={2}
              draggable={true}
              onDragMove={(e) => {
                updateElement(el.id, { 
                  leader_curve2_x: e.target.x(),
                  leader_curve2_y: e.target.y()
                });
              }}
            />
            
            {/* Arrow endpoint (terracotta) */}
            <KonvaCircle
              x={endX}
              y={endY}
              radius={8}
              fill="#BC5D41"
              stroke="#FFFFFF"
              strokeWidth={2}
              draggable={true}
              onDragMove={(e) => {
                updateElement(el.id, { 
                  leader_end_x: e.target.x(),
                  leader_end_y: e.target.y()
                });
              }}
            />
            
            {/* Guide lines from start to control points (subtle) */}
            <Line
              points={[startX, startY, curve1X, curve1Y]}
              stroke="#3B82F6"
              strokeWidth={1}
              dash={[4, 4]}
              opacity={0.5}
            />
            <Line
              points={[curve2X, curve2Y, endX, endY]}
              stroke="#A855F7"
              strokeWidth={1}
              dash={[4, 4]}
              opacity={0.5}
            />
          </>
        )}
      </Group>
    );
  }
  
  // Render shape elements
  if (el.element_type === 'shape') {
    const commonProps = {
      x: el.x,
      y: el.y,
      rotation: el.rotation || 0,
      opacity: elementOpacity,
      draggable: activeTool === 'select',
      onClick: () => setSelectedId(el.id),
      onTap: () => setSelectedId(el.id),
      onDragEnd: (e) => handleDragEnd(e, el.id),
      onTransformEnd: (e) => handleTransformEnd(e, el.id),
      ...shadowProps,
    };
    
    if (el.shape_type === 'rectangle') {
      return (
        <Rect
          key={el.id}
          id={el.id}
          {...commonProps}
          width={el.width}
          height={el.height}
          fill={el.fill_color}
          stroke={el.stroke_color}
          strokeWidth={el.stroke_width || 2}
          cornerRadius={el.corner_radius || 0}
        />
      );
    }
    
    if (el.shape_type === 'ellipse') {
      return (
        <Ellipse
          key={el.id}
          id={el.id}
          x={el.x + el.width / 2}
          y={el.y + el.height / 2}
          rotation={el.rotation || 0}
          opacity={elementOpacity}
          draggable={activeTool === 'select'}
          onClick={() => setSelectedId(el.id)}
          onTap={() => setSelectedId(el.id)}
          onDragEnd={(e) => {
            updateElement(el.id, {
              x: e.target.x() - el.width / 2,
              y: e.target.y() - el.height / 2
            });
          }}
          onTransformEnd={(e) => {
            const node = e.target;
            const scaleX = node.scaleX();
            const scaleY = node.scaleY();
            const newWidth = Math.max(20, el.width * scaleX);
            const newHeight = Math.max(20, el.height * scaleY);
            
            node.scaleX(1);
            node.scaleY(1);
            
            updateElement(el.id, {
              x: node.x() - newWidth / 2,
              y: node.y() - newHeight / 2,
              width: newWidth,
              height: newHeight,
              rotation: node.rotation()
            });
          }}
          radiusX={el.width / 2}
          radiusY={el.height / 2}
          fill={el.fill_color}
          stroke={el.stroke_color}
          strokeWidth={el.stroke_width || 2}
        />
      );
    }
    
    if (el.shape_type === 'line') {
      return (
        <Line
          key={el.id}
          id={el.id}
          x={el.x}
          y={el.y}
          points={[0, 0, el.width, 0]}
          stroke={el.stroke_color || '#1A1918'}
          strokeWidth={el.stroke_width || 4}
          rotation={el.rotation || 0}
          opacity={elementOpacity}
          draggable={activeTool === 'select'}
          onClick={() => setSelectedId(el.id)}
          onTap={() => setSelectedId(el.id)}
          onDragEnd={(e) => handleDragEnd(e, el.id)}
        />
      );
    }
    
    // Polygon shapes (triangle, star, arrow, etc.)
    if (el.shape_type === 'polygon' && el.polygon_points) {
      const pointsStr = el.polygon_points;
      const pointPairs = pointsStr.split(' ').map(p => p.split(',').map(Number));
      const scaledPoints = pointPairs.flatMap(([x, y]) => [x * el.width, y * el.height]);
      
      return (
        <Line
          key={el.id}
          id={el.id}
          x={el.x}
          y={el.y}
          points={scaledPoints}
          closed={true}
          fill={el.fill_color}
          stroke={el.stroke_color}
          strokeWidth={el.stroke_width || 2}
          rotation={el.rotation || 0}
          opacity={elementOpacity}
          draggable={activeTool === 'select'}
          onClick={() => setSelectedId(el.id)}
          onTap={() => setSelectedId(el.id)}
          onDragEnd={(e) => handleDragEnd(e, el.id)}
          onTransformEnd={(e) => handleTransformEnd(e, el.id)}
        />
      );
    }
    
    // SVG Path shapes (icons, complex shapes)
    if (el.shape_type === 'path' && el.svg_path) {
      return (
        <Rect
          key={el.id}
          id={el.id}
          x={el.x}
          y={el.y}
          width={el.width}
          height={el.height}
          rotation={el.rotation || 0}
          opacity={elementOpacity}
          draggable={activeTool === 'select'}
          onClick={() => setSelectedId(el.id)}
          onTap={() => setSelectedId(el.id)}
          onDragEnd={(e) => handleDragEnd(e, el.id)}
          onTransformEnd={(e) => handleTransformEnd(e, el.id)}
          fill={el.fill_color}
          stroke={el.stroke_color}
          strokeWidth={el.stroke_width || 2}
          sceneFunc={(context, shape) => {
            const w = shape.width();
            const h = shape.height();
            
            context.beginPath();
            const path = el.svg_path;
            const commands = path.match(/[MLQCZ][^MLQCZ]*/gi) || [];
            
            commands.forEach(cmd => {
              const type = cmd[0].toUpperCase();
              const coords = cmd.slice(1).trim().split(/[\s,]+/).map(Number);
              
              switch(type) {
                case 'M':
                  context.moveTo(coords[0] * w, coords[1] * h);
                  break;
                case 'L':
                  context.lineTo(coords[0] * w, coords[1] * h);
                  break;
                case 'Q':
                  context.quadraticCurveTo(
                    coords[0] * w, coords[1] * h,
                    coords[2] * w, coords[3] * h
                  );
                  break;
                case 'C':
                  context.bezierCurveTo(
                    coords[0] * w, coords[1] * h,
                    coords[2] * w, coords[3] * h,
                    coords[4] * w, coords[5] * h
                  );
                  break;
                case 'Z':
                  context.closePath();
                  break;
                default:
                  break;
              }
            });
            
            context.fillStrokeShape(shape);
          }}
        />
      );
    }
  }
  
  return null;
}
